import { useSearchParams } from 'react-router-dom';
import { usePercentageSupervisor } from 'entities/salary/salary-entries/salary-entries.hook';
import { Button, TableCell, TableRow, Typography } from '@mui/material';
import { GridLayout } from 'shared/ui/grid-layout';
import PriceSpan from 'shared/ui/price-span';
import { formatPersonPruning } from 'utils/person-format-helpers';
import { salaryEntriesApi } from 'entities/salary/salary-entries/salary-entries.api';
import { useSnack } from 'shared/ui/snack';

type Employee = {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  invoices: {
    countInvoices: number;
    countInvoicesClosed: number;
    managerTotalProfit: number;
    managerTotal: number;
  };
  statement: boolean;
};

type DepartmentRow = {
  id: number;
  title: string;
  manager: {
    id: number;
    firstName: string;
    lastName: string;
    middleName?: string;
  };
  plan: number;
  employees: Employee[];
  departmentStats: {
    totalProfit: number,
    percentSupervisor: number,
    statementAll: boolean,
    accrual:boolean,
}
};

const column = [
  'ФИО руководителя/название отдела',
  'План руководителя/отдела',
  'ФИО менеджера',
  '',
  'Кол-во счетов (всего/закрыто)',
  'Сумма по счетам',
  'Прибыль по счетам',
  'Расчётные проценты',
  ''];

export const PercentageSupervisorList = () => {
  const [searchParams] = useSearchParams();
  const { rows, isLoading, error } = usePercentageSupervisor(Number(searchParams.get('year')), Number(searchParams.get('month')));
  return (
    <GridLayout
      columns={column}
      items={rows}
      isLoading={isLoading}
      error={error}
      noItemsText="Список пуст"
      itemRender={(i:any) => <PercentageSupervisorListRow key={i.id} row={i} year={Number(searchParams.get('year'))} month={Number(searchParams.get('month'))}/>}
      footer={() => <PercentageSupervisorRowFooter rows={rows} />}
    />
  );
};

type RowProps = {
  row: DepartmentRow;
  year: number;
  month: number;
};

const PercentageSupervisorListRow = ({ row, year, month }: RowProps) => {
  const { showError, showSuccess } = useSnack();
  const [addSalaryPercentageSupervisor] = salaryEntriesApi.useCreatePercentageSupervisorMutation();
  const departmentTotalAmount = row.employees.reduce((acc, employee) => {
    return acc + (employee.invoices.managerTotal || 0);
  }, 0);
  const departmentTotalProfit = row.employees.reduce((acc, employee) => {
    return acc + (employee.invoices.managerTotalProfit || 0);
  }, 0);

  const handleAccruePercentage = async () => {
    await addSalaryPercentageSupervisor({
      year: year,
      month: month,
      user: row.manager
    })
      .then(() => {
        showSuccess('Проценты начислены');
      })
      .catch((e) => {
        showError('Ошибка начисления процентов: ', e);
      });
  };
  return (
    <>
      <TableRow
        key={row.id}
      >
        <TableCell>
          {row.title}
          <br />
          {formatPersonPruning(row.manager)}
        </TableCell>
        <TableCell>
          <PriceSpan price={row.plan} size="small" />
        </TableCell>
        <TableCell>
          {row.employees.map(user =>
            <>
              {formatPersonPruning(user)}<br/>
            </>
          )}
        </TableCell>
        <TableCell>
          {
            row.employees.map(user =>
              <>
                {user.statement ? '✅' : ''}<br />
              </>
            )}
        </TableCell>
        <TableCell>
          {row.employees.map(item =>
            <>
              {item.invoices.countInvoices}/{item.invoices.countInvoicesClosed} <br />
            </>
          )}
        </TableCell>
        <TableCell>
          {row.employees.map(item =>
            <>
              <PriceSpan price={item.invoices.managerTotal} size="small" /> <br />
            </>
          )}
        </TableCell>
        <TableCell>
          {row.employees.map(item =>
            <>
              <PriceSpan price={item.invoices.managerTotalProfit} size="small" /> <br />
            </>
          )}
        </TableCell>
        <TableCell>
          <PriceSpan price={row.departmentStats.percentSupervisor} size="small" />
        </TableCell>
        <TableCell />
        <TableCell>
          {
            (row.departmentStats.statementAll && !row.departmentStats.accrual) &&
           <div style={{ display: 'flex', alignItems: 'center' }}>
             <Button variant="contained" size="small" onClick={handleAccruePercentage}>Начислить</Button>
           </div>
          }
          {
            row.departmentStats.accrual && <>Начислено</>
          }

        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} align="right">
          <Typography variant="subtitle1"><b>Всего по отделу:</b></Typography>
        </TableCell>
        <TableCell>
          <PriceSpan price={departmentTotalAmount} size="small" />
        </TableCell>
        <TableCell>
          <PriceSpan price={departmentTotalProfit} size="small" />
        </TableCell>
        <TableCell />
        <TableCell>
        </TableCell>
      </TableRow>
    </>
  );
};

type PercentageSupervisorRowFooterProps = {
  rows: DepartmentRow[];
};
const PercentageSupervisorRowFooter = ({ rows }: PercentageSupervisorRowFooterProps) => {
  const totalAmount = rows.reduce((departmentAcc, department) => {
    const departmentTotal = department.employees.reduce((employeeAcc, employee) => {
      return employeeAcc + (employee.invoices.managerTotal || 0);
    }, 0);
    return departmentAcc + departmentTotal;
  }, 0);

  const totalProfit = rows.reduce((departmentAcc, department) => {
    const departmentTotal = department.employees.reduce((employeeAcc, employee) => {
      return employeeAcc + (employee.invoices.managerTotalProfit || 0);
    }, 0);
    return departmentAcc + departmentTotal;
  }, 0);

  return (
    <TableRow>
      <TableCell colSpan={4} align="right">
        <Typography variant="subtitle1">Итого:</Typography>
      </TableCell>
      <TableCell>
        <PriceSpan price={totalAmount} size="small" />
      </TableCell>
      <TableCell>
        <PriceSpan price={totalProfit} size="small" />
      </TableCell>
      <TableCell />
      <TableCell>
      </TableCell>
    </TableRow>
  );
};
